import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Star, Quote } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import SEO, { pageSEO } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await axios.get(`${API}/testimonials`);
        setTestimonials(res.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching testimonials:', error);
        }
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <div className="pt-24 md:pt-32" data-testid="testimonials-page">
      <SEO {...pageSEO.testimonials} />
      {/* Hero */}
      <section className="section-padding bg-[#243b5a] text-white">
        <div className="container-max text-center">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Guest Stories</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 mb-6">
            <span className="text-[#D4AF37]">Testimonials</span>
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Hear from travelers who have experienced the magic of India with us.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-[#F9F9F7]">
        <div className="container-max">
          <div className="max-w-4xl mx-auto space-y-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.id} 
                className="bg-white rounded-2xl p-8 md:p-12 shadow-soft relative overflow-hidden"
                data-testid={`testimonial-${index}`}
              >
                {/* Quote mark */}
                <div className="absolute top-6 right-8 opacity-10">
                  <Quote className="w-24 h-24 text-[#D4AF37]" />
                </div>
                
                {/* Stars */}
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className="fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="font-serif text-xl md:text-2xl text-[#1A1A1A] leading-relaxed italic mb-8 relative z-10">
                  "{testimonial.quote}"
                </blockquote>

                {/* Author */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#243b5a] flex items-center justify-center text-white font-serif text-lg">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-[#243b5a]">— {testimonial.name}, {testimonial.location}</p>
                    <p className="text-[#888888] text-sm">{testimonial.trip_date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto text-center">
            <div>
              <div className="font-serif text-5xl text-[#243b5a] mb-2">100%</div>
              <p className="text-[#4A4A4A]">Guest Satisfaction</p>
            </div>
            <div>
              <div className="font-serif text-5xl text-[#243b5a] mb-2">5.0</div>
              <div className="flex items-center justify-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-[#D4AF37] text-[#D4AF37]" />
                ))}
              </div>
              <p className="text-[#4A4A4A]">Average Rating</p>
            </div>
            <div>
              <div className="font-serif text-5xl text-[#243b5a] mb-2">35+</div>
              <p className="text-[#4A4A4A]">Years of India Experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#243b5a]">
        <div className="container-max text-center text-white">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
            Ready to Create Your <span className="text-[#D4AF37]">Own Story</span>?
          </h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
            Join our next departure and experience the magic of India for yourself.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#243b5a] font-semibold px-8 py-3 rounded-md transition-all" data-testid="inquire-now-testimonials-btn">
                Inquire Now
              </Button>
            </Link>
            <Link to="/pricing">
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#243b5a] font-semibold px-8 py-3 rounded-md transition-all">
                View Dates & Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
